import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AuthShell from '../components/AuthShell';
import { errorBox, input, label, link, primaryBtn } from '../components/authStyles';

const API_URL = process.env.REACT_APP_API_URL || '';

export function errorMessage(err, fallback) {
  const detail = err.response?.data?.detail;
  if (Array.isArray(detail)) return detail.map((d) => d.msg).join('; ');
  return detail || fallback;
}

// Etapas: login (e-mail+senha) -> otp (2FA) | signup (e-mail) -> signup-sent | forgot -> forgot-sent
function Login({ onLogin }) {
  const [step, setStep] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [challengeId, setChallengeId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const goTo = (next) => {
    setStep(next);
    setError('');
    setCode('');
  };

  const run = async (fn, fallback) => {
    setLoading(true);
    setError('');
    try {
      await fn();
    } catch (err) {
      setError(errorMessage(err, fallback));
    } finally {
      setLoading(false);
    }
  };

  const submitLogin = (e) => {
    e.preventDefault();
    run(async () => {
      const { data } = await axios.post(`${API_URL}/api/auth/login`, { email, password });
      setChallengeId(data.challenge_id);
      goTo('otp');
    }, 'Não foi possível entrar');
  };

  const submitOtp = (e) => {
    e.preventDefault();
    run(async () => {
      const { data } = await axios.post(`${API_URL}/api/auth/login/verify`, {
        challenge_id: challengeId,
        code,
      });
      onLogin(data.access_token, data.user);
      navigate('/dashboard');
    }, 'Código inválido');
  };

  const resendOtp = () => {
    run(async () => {
      const { data } = await axios.post(`${API_URL}/api/auth/login`, { email, password });
      setChallengeId(data.challenge_id);
      setCode('');
    }, 'Não foi possível reenviar o código');
  };

  const submitSignup = (e) => {
    e.preventDefault();
    run(async () => {
      await axios.post(`${API_URL}/api/auth/signup`, { email });
      goTo('signup-sent');
    }, 'Não foi possível enviar o link');
  };

  const submitForgot = (e) => {
    e.preventDefault();
    run(async () => {
      await axios.post(`${API_URL}/api/auth/password/forgot`, { email });
      goTo('forgot-sent');
    }, 'Não foi possível enviar o link');
  };

  const titles = {
    login: 'Entrar',
    otp: 'Verificação em duas etapas',
    signup: 'Criar conta',
    'signup-sent': 'Verifique seu e-mail',
    forgot: 'Esqueci minha senha',
    'forgot-sent': 'Verifique seu e-mail',
  };

  return (
    <AuthShell title={titles[step]}>
      {error && <div className={errorBox}>{error}</div>}

          {step === 'login' && (
            <form onSubmit={submitLogin} className="flex flex-col gap-4">
              <label className={label}>
                E-mail
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="seu@email.com" autoComplete="username" className={input} />
              </label>
              <label className={label}>
                Senha
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password" className={input} />
              </label>
              <button type="submit" className={primaryBtn} disabled={loading}>
                {loading ? 'Entrando…' : 'Entrar'}
              </button>
              <p className="text-center text-sm">
                <span className={link} onClick={() => goTo('forgot')}>Esqueci minha senha</span>
              </p>
              <p className="text-center text-sm text-slate-400">
                Não tem conta? <span className={link} onClick={() => goTo('signup')}>Criar conta</span>
              </p>
            </form>
          )}

          {step === 'otp' && (
            <form onSubmit={submitOtp} className="flex flex-col gap-4">
              <p className="text-sm text-slate-400">
                Enviamos um código de 6 dígitos para <strong className="text-slate-100">{email}</strong>.
              </p>
              <label className={label}>
                Código
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  required
                  placeholder="000000"
                  className={`${input} text-center text-2xl tracking-[0.5em]`}
                  autoFocus
                />
              </label>
              <button type="submit" className={primaryBtn} disabled={loading || code.length !== 6}>
                {loading ? 'Verificando…' : 'Verificar e entrar'}
              </button>
              <p className="text-center text-sm text-slate-400">
                <span className={link} onClick={resendOtp}>Reenviar código</span>
                {' · '}
                <span className={link} onClick={() => goTo('login')}>Voltar</span>
              </p>
            </form>
          )}

          {step === 'signup' && (
            <form onSubmit={submitSignup} className="flex flex-col gap-4">
              <p className="text-sm text-slate-400">
                Informe seu e-mail. Você receberá um link para confirmar e definir nome e senha.
              </p>
              <label className={label}>
                E-mail
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="seu@email.com" className={input} />
              </label>
              <button type="submit" className={primaryBtn} disabled={loading}>
                {loading ? 'Enviando…' : 'Enviar link de cadastro'}
              </button>
              <p className="text-center text-sm text-slate-400">
                Já tem conta? <span className={link} onClick={() => goTo('login')}>Entrar</span>
              </p>
            </form>
          )}

          {step === 'signup-sent' && (
            <div>
              <p className="text-sm leading-relaxed text-slate-300">
                Se <strong className="text-slate-100">{email}</strong> puder ser cadastrado, enviamos um link de confirmação.
                Abra o e-mail e clique no link para concluir o cadastro. O link expira em 30 minutos.
              </p>
              <p className="mt-5 text-center text-sm">
                <span className={link} onClick={() => goTo('login')}>Voltar para o login</span>
              </p>
            </div>
          )}

          {step === 'forgot' && (
            <form onSubmit={submitForgot} className="flex flex-col gap-4">
              <p className="text-sm text-slate-400">
                Informe seu e-mail. Enviaremos um link para você definir uma nova senha.
              </p>
              <label className={label}>
                E-mail
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="seu@email.com" className={input} />
              </label>
              <button type="submit" className={primaryBtn} disabled={loading}>
                {loading ? 'Enviando…' : 'Enviar link'}
              </button>
              <p className="text-center text-sm">
                <span className={link} onClick={() => goTo('login')}>Voltar para o login</span>
              </p>
            </form>
          )}

          {step === 'forgot-sent' && (
            <div>
              <p className="text-sm leading-relaxed text-slate-300">
                Se houver uma conta para <strong className="text-slate-100">{email}</strong>, enviamos um link para definir a senha.
                Se ainda não houver conta, enviamos o link de cadastro. O link expira em 30 minutos.
              </p>
              <p className="mt-5 text-center text-sm">
                <span className={link} onClick={() => goTo('login')}>Voltar para o login</span>
              </p>
            </div>
          )}
    </AuthShell>
  );
}

export default Login;
